import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { Column } from 'components/shared/Layout';
import { legShape } from 'constants/propTypes';
import { directions, OUTBOUND } from 'constants/booking';

import SegmentOrdering from './SegmentOrdering';

export const SegmentSummaryColumnRow = ({
  pendingCancellationLegs,
  pendingCreationLegs,
  direction,
  currency,
  trainMarginDeprecated,
  hasOldTickets,
  hasNewTickets,
  isCancelled = false,
  order = 0,
  isMiceGroup
}) => {
  const isOutbound = direction === OUTBOUND;
  const cells = 3;
  const orderedBase = order * cells + (isOutbound ? 0 : 1);

  const hasPendingCancellationLegs = Boolean(pendingCancellationLegs.length);
  const hasPendingCreationLegs = Boolean(pendingCreationLegs.length);

  const baseCyName = isCancelled ? 'cancellation' : 'creation';

  if ((isCancelled && !hasOldTickets) || (!isCancelled && !hasNewTickets)) {
    return <Column span={8} data-cy={`segment-summary-${baseCyName}-${direction}`} order={orderedBase + 1} />;
  }

  return (
    <>
      {isOutbound && (
        <Column span={16} data-cy={`segment-summary-${baseCyName}-header`} order={orderedBase}>
          <h2>
            {isCancelled ? (
              <FormattedMessage id="journeySegment.pendingCancellationHeader" />
            ) : (
              <FormattedMessage id="journeySegment.pendingCreationHeader" />
            )}
          </h2>
        </Column>
      )}

      <Column
        span={8}
        data-cy={`segment-summary-${baseCyName}-${direction}`}
        className={`segment-summary-${direction}`}
        order={orderedBase + 1}
      >
        {isCancelled && hasPendingCancellationLegs && (
          <>
            {pendingCancellationLegs.map((cancellationLeg) => (
              <SegmentOrdering
                key={cancellationLeg.id}
                leg={cancellationLeg}
                addOnLeg={cancellationLeg.addOnLeg}
                currency={currency}
                trainMarginDeprecated={trainMarginDeprecated}
                direction={direction}
                isMiceGroup={isMiceGroup}
              />
            ))}
          </>
        )}
        {!isCancelled && hasPendingCreationLegs && (
          <>
            {pendingCreationLegs.map((creationLeg) => (
              <SegmentOrdering
                key={creationLeg.id}
                leg={creationLeg}
                addOnLeg={creationLeg.addOnLeg}
                currency={currency}
                trainMarginDeprecated={trainMarginDeprecated}
                direction={direction}
                isMiceGroup={isMiceGroup}
              />
            ))}
          </>
        )}
      </Column>
    </>
  );
};

SegmentSummaryColumnRow.propTypes = {
  currency: PropTypes.string,
  direction: PropTypes.oneOf(directions).isRequired,
  pendingCancellationLegs: PropTypes.arrayOf(legShape),
  pendingCreationLegs: PropTypes.arrayOf(legShape),
  trainMarginDeprecated: PropTypes.number,
  hasOldTickets: PropTypes.bool,
  hasNewTickets: PropTypes.bool,
  order: PropTypes.number,
  isCancelled: PropTypes.bool,
  isMiceGroup: PropTypes.bool
};

const SegmentSummaryColumn = ({
  pendingCancellationLegs = [],
  pendingCreationLegs = [],
  direction,
  currency,
  trainMarginDeprecated,

  hasOldTickets = false,
  hasNewTickets = false,
  cancelledFirst = false,
  isMiceGroup
}) => (
  <>
    <SegmentSummaryColumnRow
      pendingCancellationLegs={pendingCancellationLegs}
      pendingCreationLegs={pendingCreationLegs}
      direction={direction}
      currency={currency}
      trainMarginDeprecated={trainMarginDeprecated}
      hasOldTickets={hasOldTickets}
      hasNewTickets={hasNewTickets}
      isCancelled={cancelledFirst}
      isMiceGroup={isMiceGroup}
    />
    <SegmentSummaryColumnRow
      pendingCancellationLegs={pendingCancellationLegs}
      pendingCreationLegs={pendingCreationLegs}
      direction={direction}
      currency={currency}
      trainMarginDeprecated={trainMarginDeprecated}
      hasOldTickets={hasOldTickets}
      hasNewTickets={hasNewTickets}
      isCancelled={!cancelledFirst}
      isMiceGroup={isMiceGroup}
      order={1}
    />
  </>
);

SegmentSummaryColumn.propTypes = {
  cancelledFirst: PropTypes.bool,
  currency: PropTypes.string,
  direction: PropTypes.oneOf(directions).isRequired,
  pendingCancellationLegs: PropTypes.arrayOf(legShape),
  pendingCreationLegs: PropTypes.arrayOf(legShape),
  trainMarginDeprecated: PropTypes.number,
  hasOldTickets: PropTypes.bool,
  hasNewTickets: PropTypes.bool,
  isMiceGroup: PropTypes.bool
};

export default SegmentSummaryColumn;
